/**
 * Easter painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterPaintingIdeasPack: ToilePack = {
    slug: 'easter-painting-ideas',
    category: 'craft',
    name: 'Easter painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'eggs', label: 'Egg nest', subject: 'a canvas of pastel painted eggs in a twig nest among small spring flowers'},
        {key: 'cross', label: 'Easter dawn', subject: 'a canvas of white lilies before a plain empty cross on a hill at sunrise, painted with reverence'},
        {key: 'bunny', label: 'Tulip bunny', subject: 'a canvas of a small brown rabbit sitting in a field of pink and yellow tulips'},
    ],
    styles: [
        {key: 'pastel', label: 'Soft pastel', style: 'painted in soft pastel acrylic with smooth blended colour and simple shapes', palette: 'Pale pink, mint, lemon and sky blue.', framing: 'The canvas on a wooden table easel, shot straight on at eye level and centred.'},
        {key: 'washes', label: 'Watercolor look', style: 'painted in thinned acrylic washes for a loose watercolour look, the white canvas showing through', palette: 'Transparent spring greens, lilac and gold.', framing: 'Leaning on a white shelf beside a small vase of tulips, shot at eye level.'},
        {key: 'knife', label: 'Palette knife', style: 'painted with a palette knife in thick textured strokes of paint, the flowers raised from the surface', palette: 'Fresh greens with bright yellow, white and coral.', framing: 'Hung on a white-painted wall above a table set for Easter brunch, shot at eye level.'},
    ],
    directions: ['baby chicks', 'lamb', 'cherry blossom'],
    directionHint: 'try: baby chicks',
    seo: {
        title: 'Easter Painting Ideas: Egg Nest, Dawn Cross, Tulip Bunny',
        metaDescription: 'Nine Easter painting ideas: pastel eggs in a nest, lilies before an empty cross at dawn and a bunny in tulips, in three styles.',
        h1: 'Easter painting ideas: painted eggs in a nest, lilies at an empty cross, tulip bunny',
        tagline: 'Nine Easter canvases, three spring subjects, each in soft pastel, a thin wash look or palette-knife texture.',
        intro: "Easter painting is spring painting, for the children's table and for the faithful who paint the day itself. These nine show three subjects: pastel painted eggs in a twig nest among small spring flowers, white lilies before a plain empty cross on a hill at sunrise, and a small brown rabbit in a field of pink and yellow tulips. Type another subject, such as baby chicks or a lamb, and the same spring look carries it.",
        useCases: [
            "a children's table canvas",
            'a spring mantel picture',
            'a church group art project',
            'a gift for Easter Sunday',
        ],
        faq: [
            {q: 'Is the cross picture religious art?', a: 'Yes. It paints the empty cross on a hill at dawn, in a quiet and reverent style.'},
            {q: 'What is the watercolor look?', a: 'Thinned acrylic washes in spring greens, lilac and gold, with the white canvas showing through.'},
            {q: 'Which subject suits children?', a: "The egg nest and the tulip bunny are gentle, bright subjects for a children's table."},
            {q: 'Can I change the subject?', a: 'Yes. Type another subject and the spring palette and the three styles carry it.'},
        ],
    },
}
